/**
 * Color palette definitions for light and dark themes
 * Centralized color management for easy theme switching
 */

export interface ColorPalette {
  // Primary colors
  primary: string;
  primaryDark: string;
  primaryLight: string;
  
  // Gradient colors
  gradient: {
    primaryStart: string;
    primaryEnd: string;
  };
  
  // Button colors
  button: {
    border: string;
    text: string;
    gradientStart: string;
    gradientEnd: string;
  };
  
  // Bank details colors
  bankDetails: {
    text: string;
  };
  
  // Confirmation page colors
  confirmation: {
    submitBg: string;
    submitBorder: string;
    cancelBg: string;
    cancelBorder: string;
  };
  
  // Offline modal colors
  offline: {
    primaryBg: string;
    secondaryBorder: string;
    secondaryText: string;
  };
  
  // Secondary colors
  secondary: string;
  secondaryLight: string;
  secondaryDark: string;
  
  // Status colors
  success: string;
  successLight: string;
  successDark: string;
  
  error: string;
  errorLight: string;
  errorDark: string;
  
  warning: string;
  warningLight: string;
  warningDark: string;
  warningRed: string;
  
  info: string;
  infoLight: string;
  infoBlue: string;
  infoDark: string;
  
  // Neutral colors
  dark: string;
  grayDarkerFont: string;
  grayDark: string;
  gray: string;
  grayLight: string;
  grayLimitFont: string;
  grayLighter: string;
  light: string;
  white: string;
  
  // Text colors
  text: {
    primary: string;
    secondary: string;
    light: string;
    muted: string;
    error: string;
    label: string;
    inputColor: string;
  };
  
  // Background colors
  bgPrimary: string;
  bgSecondary: string;
  bgDark: string;
  bgLight: string;
  accordionBg: string;

  // Checkbox colors
  checkedColor: string;
  checkedTickColor: string;
}

export const lightColors: ColorPalette = {
  // Premium Primary Colors - Purple/Blue brand
  primary: '#9D8AEC',
  primaryDark: '#8064E6',
  primaryLight: '#B5A3F0',
  
  gradient: {
    primaryStart: '#844BC5',
    primaryEnd: '#7D77FF',
  },

  checkedColor: 'rgb(244, 246, 253)',
  checkedTickColor: '#2C2C2C',
  
  // Button colors
  button: {
    border: env.light.buttonBorder || 'rgb(128, 100, 230)',
    text: env.light.buttonText || '#FFFFFF',
    gradientStart: env.light.buttonGradientStart || 'rgb(132, 75, 197)',
    gradientEnd: env.light.buttonGradientEnd || 'rgb(125, 119, 255)',
  },
  
  // Bank details colors
  bankDetails: {
    text: env.light.bankDetailsText || 'rgb(127, 104, 235)',
  },
  
  // Confirmation page colors
  confirmation: {
    submitBg: env.light.confirmationSubmitBg || 'rgb(127, 104, 235)',
    submitBorder: env.light.confirmationSubmitBorder || 'rgb(128, 100, 230)',
    cancelBg: env.light.confirmationCancelBg || 'rgb(244, 246, 253)',
    cancelBorder: env.light.confirmationCancelBorder || 'rgb(216, 221, 236)',
  },
  
  // Offline modal colors
  offline: {
    primaryBg: env.light.offlinePrimaryBg || 'rgb(255, 0, 34)',
    secondaryBorder: env.light.offlineSecondaryBorder || 'rgb(128, 100, 230)',
    secondaryText: env.light.offlineSecondaryText || 'rgb(128, 100, 230)',
  },
  
  secondary: '#003366',
  secondaryLight: '#1a4d80',
  secondaryDark: '#002244',
  
  // Premium Success Colors - Exact match
  success: '#067647', // Premium green
  successLight: '#d4edda',
  successDark: '#067647',
  
  // Premium Error Colors - Exact match
  error: '#d92d20', // Premium red
  errorLight: '#FEF3F2', // Premium error background
  errorDark: '#B42318', // Premium error dark
  
  warning: '#ffc107',
  warningLight: '#fff3cd',
  warningDark: '#e0a800',
  warningRed: '#ff0022',
  
  info: '#17a2b8',
  infoLight: '#d1ecf1',
  infoBlue: 'rgb(24, 107, 232)', // Premium blue
  infoDark: '#138496',
  
  // Premium Neutral Colors - Exact match
  dark: '#6c757d',
  grayDarkerFont: 'rgb(51, 51, 51)', // #333333 - Premium text primary
  grayDark: '#495057',
  gray: '#6c757d',
  grayLight: 'rgb(216, 221, 236)', // #D8DDEC - Premium border color
  grayLimitFont: '#4e5d6c',
  grayLighter: '#e9ecef',
  light: 'rgb(244, 246, 253)', // #F4F6FD - Premium background
  white: 'rgb(255, 255, 255)', // #FFFFFF
  
  // Premium Text Colors - Exact match
  text: {
    primary: 'rgb(51, 51, 51)', // #333333 - Premium primary text (light mode)
    secondary: '#7F68EB',
    light: '#FFFFFF',
    muted: 'rgb(125, 125, 125)', // #7D7D7D - Premium muted text
    error: '#d92d20',
    label: '#2d2d3f',
    inputColor: 'rgb(125, 125, 125)', // #7D7D7D
  },
  
  // Premium Background Colors - Exact match
  bgPrimary: 'rgb(255, 255, 255)', // #FFFFFF - Card background
  bgSecondary: 'rgb(244, 246, 253)', // #F4F6FD - Page background
  bgDark: '#212529',
  bgLight: '#ebe3fb',
  accordionBg: 'rgb(255, 255, 255)', // Light white accordion background for light theme
};

/**
 * Dark mode colors loaded from environment variables
 * Brand-specific themes (standard = purple/blue, lf = teal/gold)
 */

import { env } from '@/config/env';

// Brand-specific fallback colors
const standardBrandDefaults: ColorPalette = {
  // Standard Brand - Purple/Blue
  primary: '#9D8AEC',
  primaryDark: '#8064E6',
  primaryLight: '#B5A3F0',

  gradient: {
    primaryStart: '#9D8AEC',
    primaryEnd: '#9B95FF',
  },

  // Button colors
  button: {
    border: '#9D8AEC',
    text: '#FFFFFF',
    gradientStart: '#9D8AEC',
    gradientEnd: '#9B95FF',
  },

  // Bank details colors
  bankDetails: {
    text: '#B5A3F0',
  },

  // Confirmation page colors
  confirmation: {
    submitBg: '#9D8AEC',
    submitBorder: '#8064E6',
    cancelBg: '#2C2C2C',
    cancelBorder: '#364152',
  },

  // Offline modal colors
  offline: {
    primaryBg: 'rgb(255, 0, 34)',
    secondaryBorder: '#9D8AEC',
    secondaryText: '#B5A3F0',
  },

  checkedColor: '#9D8AEC',
  checkedTickColor: '#FFFFFF',

  secondary: '#4A90E2',
  secondaryLight: '#6BA8F0',
  secondaryDark: '#2E6BB8',

  success: '#4CAF50',
  successLight: '#2d4a2f',
  successDark: '#66BB6A',

  error: '#EF5350',
  errorLight: '#4a2d2e',
  errorDark: '#F44336',

  warning: '#FFB74D',
  warningLight: '#4a3d2d',
  warningDark: '#FFA726',
  warningRed: '#ff4466',

  info: '#29B6F6',
  infoLight: '#2d3d4a',
  infoBlue: '#42A5F5',
  infoDark: '#03A9F4',

  dark: '#ffffff',
  grayDarkerFont: '#ffffff',
  grayDark: '#ffffff',
  gray: '#ffffff',
  grayLight: '#364152',
  grayLimitFont: '#ffffff',
  grayLighter: '#424242',
  light: '#2C2C2C',
  white: '#1E1E1E',

  text: {
    primary: '#ffffff',
    secondary: '#B5A3F0',
    light: '#ffffff',
    muted: '#ffffff',
    error: '#EF5350',
    label: '#ffffff',
    inputColor: '#CDD5DF',
  },

  bgPrimary: '#121926',
  bgSecondary: '#121926',
  bgDark: '#ffffff',
  bgLight: '#2d2540',
  accordionBg: '#0D121C',
};

const lfBrandDefaults: ColorPalette = {
  // LF Brand - Teal/Gold
  primary: '#8B7D2F',
  primaryDark: '#6B5D1F',
  primaryLight: '#9B8D3F',

  gradient: {
    primaryStart: '#8B7D2F',
    primaryEnd: '#9B8D3F',
  },

  // Button colors
  button: {
    border: '#8B7D2F',
    text: '#FFFFFF',
    gradientStart: '#8B7D2F',
    gradientEnd: '#9B8D3F',
  },

  // Bank details colors
  bankDetails: {
    text: '#C9A841',
  },

  // Confirmation page colors
  confirmation: {
    submitBg: '#C9A841',
    submitBorder: '#B8982E',
    cancelBg: '#1A4444',
    cancelBorder: '#2D5555',
  },

  // Offline modal colors
  offline: {
    primaryBg: 'rgb(255, 0, 34)',
    secondaryBorder: '#C9A841',
    secondaryText: '#C9A841',
  },

  checkedColor: '#8B7D2F',
  checkedTickColor: '#FFFFFF',

  secondary: '#C9A841',
  secondaryLight: '#FFD700',
  secondaryDark: '#9B8B3D',

  success: '#4CAF50',
  successLight: '#2d4a2f',
  successDark: '#66BB6A',

  error: '#EF5350',
  errorLight: '#4a2d2e',
  errorDark: '#F44336',

  warning: '#FFD700',
  warningLight: '#FFF8DC',
  warningDark: '#C9A841',
  warningRed: '#ff4466',

  info: '#FFD700',
  infoLight: '#2d3d4a',
  infoBlue: '#C9A841',
  infoDark: '#9B8B3D',

  dark: '#FFFFFF',
  grayDarkerFont: '#FFFFFF',
  grayDark: '#E0E0E0',
  gray: '#C0C0C0',
  grayLight: '#2D5555',
  grayLimitFont: '#FFD700',
  grayLighter: '#1A4444',
  light: '#103333',
  white: '#0A2F2F',

  text: {
    primary: '#FFFFFF',
    secondary: '#C9A841',
    light: '#FFFFFF',
    muted: '#B0B0B0',
    error: '#EF5350',
    label: '#C9A841',
    inputColor: '#7D9999',
  },

  bgPrimary: '#0D3535',
  bgSecondary: '#0A2F2F',
  bgDark: '#FFFFFF',
  bgLight: '#1A4848',
  accordionBg: '#0A2828',
};

// Select fallback defaults based on brand
const fallbackDefaults = env.brand === 'lf' ? lfBrandDefaults : standardBrandDefaults;

export const darkColors: ColorPalette = {
  primary: env.dark.primary || fallbackDefaults.primary,
  primaryDark: env.dark.primaryDark || fallbackDefaults.primaryDark,
  primaryLight: env.dark.primaryLight || fallbackDefaults.primaryLight,
  checkedColor: env.dark.checkedColor || fallbackDefaults.checkedColor,
  checkedTickColor: env.dark.checkedTickColor || fallbackDefaults.checkedTickColor,

  gradient: {
    primaryStart: env.dark.gradientStart || fallbackDefaults.gradient.primaryStart,
    primaryEnd: env.dark.gradientEnd || fallbackDefaults.gradient.primaryEnd,
  },

  // Button colors
  button: {
    border: env.dark.buttonBorder || fallbackDefaults.button.border,
    text: env.dark.buttonText || fallbackDefaults.button.text,
    gradientStart: env.dark.buttonGradientStart || fallbackDefaults.button.gradientStart,
    gradientEnd: env.dark.buttonGradientEnd || fallbackDefaults.button.gradientEnd,
  },

  // Bank details colors
  bankDetails: {
    text: env.dark.bankDetailsText || fallbackDefaults.bankDetails.text,
  },

  // Confirmation page colors
  confirmation: {
    submitBg: env.dark.confirmationSubmitBg || fallbackDefaults.confirmation.submitBg,
    submitBorder: env.dark.confirmationSubmitBorder || fallbackDefaults.confirmation.submitBorder,
    cancelBg: env.dark.confirmationCancelBg || fallbackDefaults.confirmation.cancelBg,
    cancelBorder: env.dark.confirmationCancelBorder || fallbackDefaults.confirmation.cancelBorder,
  },

  // Offline modal colors
  offline: {
    primaryBg: env.dark.offlinePrimaryBg || fallbackDefaults.offline.primaryBg,
    secondaryBorder: env.dark.offlineSecondaryBorder || fallbackDefaults.offline.secondaryBorder,
    secondaryText: env.dark.offlineSecondaryText || fallbackDefaults.offline.secondaryText,
  },

  secondary: env.dark.secondary || fallbackDefaults.secondary,
  secondaryLight: env.dark.secondaryLight || fallbackDefaults.secondaryLight,
  secondaryDark: env.dark.secondaryDark || fallbackDefaults.secondaryDark,

  success: env.dark.success || fallbackDefaults.success,
  successLight: env.dark.successLight || fallbackDefaults.successLight,
  successDark: env.dark.successDark || fallbackDefaults.successDark,

  error: env.dark.error || fallbackDefaults.error,
  errorLight: env.dark.errorLight || fallbackDefaults.errorLight,
  errorDark: env.dark.errorDark || fallbackDefaults.errorDark,

  warning: env.dark.warning || fallbackDefaults.warning,
  warningLight: env.dark.warningLight || fallbackDefaults.warningLight,
  warningDark: env.dark.warningDark || fallbackDefaults.warningDark,
  warningRed: env.dark.warningRed || fallbackDefaults.warningRed,

  info: env.dark.info || fallbackDefaults.info,
  infoLight: env.dark.infoLight || fallbackDefaults.infoLight,
  infoBlue: env.dark.infoBlue || fallbackDefaults.infoBlue,
  infoDark: env.dark.infoDark || fallbackDefaults.infoDark,

  dark: env.dark.dark || fallbackDefaults.dark,
  grayDarkerFont: env.dark.grayDarkerFont || fallbackDefaults.grayDarkerFont,
  grayDark: env.dark.grayDark || fallbackDefaults.grayDark,
  gray: env.dark.gray || fallbackDefaults.gray,
  grayLight: env.dark.grayLight || fallbackDefaults.grayLight,
  grayLimitFont: env.dark.grayLimitFont || fallbackDefaults.grayLimitFont,
  grayLighter: env.dark.grayLighter || fallbackDefaults.grayLighter,
  light: env.dark.light || fallbackDefaults.light,
  white: env.dark.white || fallbackDefaults.white,

  text: {
    primary: env.dark.textPrimary || fallbackDefaults.text.primary,
    secondary: env.dark.textSecondary || fallbackDefaults.text.secondary,
    light: env.dark.textLight || fallbackDefaults.text.light,
    muted: env.dark.textMuted || fallbackDefaults.text.muted,
    error: env.dark.textError || fallbackDefaults.text.error,
    label: env.dark.textLabel || fallbackDefaults.text.label,
    inputColor: env.dark.textInputColor || fallbackDefaults.text.inputColor,
  },

  bgPrimary: env.dark.bgPrimary || fallbackDefaults.bgPrimary,
  bgSecondary: env.dark.bgSecondary || fallbackDefaults.bgSecondary,
  bgDark: env.dark.bgDark || fallbackDefaults.bgDark,
  bgLight: env.dark.bgLight || fallbackDefaults.bgLight,
  accordionBg: env.dark.accordionBg || fallbackDefaults.accordionBg,
};

